import { CopyOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { useGetOfferQuery } from "api/TenantSlug/Offer/hooks";
import { OfferFormDetailForm } from "components/Forms/OfferFormDetailForm/OfferFormDetailForm";
import { OfferDetail } from "components/other/OfferDetail/OfferDetail";
import { ActionTitle } from "components/UI/ActionTitle/ActionTitle.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { useState } from "react";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums.ts";

export const OfferFormDetail = ({ offerId }: { offerId: string }) => {
    const { data, isLoading, isError } = useGetOfferQuery(offerId, {
        filter: { include: "forms,formStructures,affiliates" },
    });
    const offer = data?.offer;
    const { t: tF } = usePaldockTranslation("components.offer-form-detail");
    const { t: tC } = usePaldockTranslation("common");
    const navigate = useNavigate();
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const tenantSlug = userUtils.getActiveTenant()?.slug_name ?? "";
    const canEdit = permissionUtils.hasPermission(PermissionEnum.OfferEditIframe);

    const [currentIframe, setCurrentIframe] = useState<string>("");

    const handleCopy = () => {
        if (currentIframe) {
            navigator.clipboard
                .writeText(currentIframe)
                .then(() => toast.info(tF("messages.code_copied")))
                .catch(() => toast.error(tF("messages.code_copy_error")));
        } else {
            toast.error(tF("messages.no_code_to_copy"));
        }
    };

    const handleEdit = () => {
        if (offer) {
            navigate({
                to: "/$tenantSlug/offer/edit/$offerId/form",
                params: { tenantSlug, offerId: offer.id },
            });
        }
    };

    if (!offer) {
        return <QueryState isLoading={isLoading} isError={isError || !isLoading} errorKey="errors.loading_offer" />;
    }

    const hasForms = offer.forms && offer.forms.length > 0;

    return (
        <OfferDetail offer={offer}>
            <SectionContentWrapper>
                <ActionTitle
                    title={{
                        text: tF("title"),
                        level: 3,
                    }}
                    buttons={[
                        ...(canEdit
                            ? [
                                  {
                                      text: tC("edit"),
                                      icon: <EditOutlined />,
                                      type: "default" as const,
                                      callback: handleEdit,
                                  },
                              ]
                            : []),
                        ...(hasForms
                            ? [
                                  {
                                      text: tC("copy"),
                                      icon: <CopyOutlined />,
                                      callback: handleCopy,
                                  },
                              ]
                            : []),
                    ]}
                />
                <OfferFormDetailForm
                    key={offer.id}
                    offer={offer}
                    showUserSelect={true}
                    onIframeChange={setCurrentIframe}
                />
            </SectionContentWrapper>
        </OfferDetail>
    );
};
